import { c as _c } from "react/compiler-runtime";
import { range } from 'betterplace-utils';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useValueRef } from '../../hooks';
import styles from './Confetti.module.css';
import { Particle as ParticleType, getConfettiKeyframeStyles, getConfettoStyle, rotationKeyframeStyles } from './styles';
import { rotationTransforms, shouldBeCircle } from './utils';
const FORCE = 0.5; // 0-1 roughly the vertical force at which particles initially explode
const SIZE = 12; // max height for particle rectangles, diameter for particle circles
const HEIGHT = '120vh'; // distance particles will fall from initial explosion point
const WIDTH = 1000; // horizontal spread of particles in pixels
const PARTICLE_COUNT = 100;
const DURATION = 2200;
const COLORS = ['#FFC700', '#FF0000', '#2E3191', '#41BBC7'];
export type ConfettiProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'ref'> & {
  particleCount?: number;
  duration?: number;
  colors?: string[];
  particleSize?: number;
  force?: number;
  height?: number | string;
  width?: number;
  zIndex?: number;
  onComplete?: () => void;
};
function createParticles(count: number, colors: string[]): ParticleType[] {
  const increment = 360 / count;
  return Array.from(range(count)).map(index => ({
    color: colors[index % colors.length]!,
    degree: increment * index
  }));
}
function getRandomRotationIndex() {
  return Math.round(Math.random() * (rotationTransforms.length - 1));
}
function useRandomRotationIndex() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getRandomRotationIndex();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const rotationIndex = t0;
  return rotationIndex;
}
function Particle({
  particle,
  duration,
  force,
  particleSize
}: {
  particle: ParticleType;
  duration: number;
  force: number;
  particleSize: number;
}) {
  const rotationIndex = useRandomRotationIndex();
  const isCircle = shouldBeCircle(rotationIndex);
  const particleStyles = getConfettoStyle(particle, duration, force, particleSize, isCircle);
  return <div style={particleStyles} className={classNames(styles.particle, styles[`rotation-${particle.degree}`], {
    [styles.circle!]: isCircle
  })}>
      <div></div>
    </div>;
}
function Confetti(t0) {
  const $ = _c(37);
  let onComplete;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let zIndex;
  if ($[0] !== t0) {
    ({
      particleCount: t1,
      duration: t2,
      colors: t3,
      particleSize: t4,
      force: t5,
      height: t6,
      width: t7,
      zIndex,
      onComplete,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onComplete;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = zIndex;
  } else {
    onComplete = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    zIndex = $[10];
  }
  const particleCount = t1 === undefined ? PARTICLE_COUNT : t1;
  const duration = t2 === undefined ? DURATION : t2;
  const colors = t3 === undefined ? COLORS : t3;
  const particleSize = t4 === undefined ? SIZE : t4;
  const force = t5 === undefined ? FORCE : t5;
  const height = t6 === undefined ? HEIGHT : t6;
  const width = t7 === undefined ? WIDTH : t7;
  const [origin, setOrigin] = useState();
  const onCompleteRef = useValueRef(onComplete);
  let t8;
  if ($[11] !== colors || $[12] !== particleCount) {
    t8 = createParticles(particleCount, colors);
    $[11] = colors;
    $[12] = particleCount;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const particles = t8;
  let t9;
  if ($[14] !== particles) {
    t9 = particles.map(_temp);
    $[14] = particles;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const degrees = t9;
  let t10;
  if ($[16] !== degrees || $[17] !== height || $[18] !== width) {
    t10 = getConfettiKeyframeStyles(degrees, height, width);
    $[16] = degrees;
    $[17] = height;
    $[18] = width;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const keyframeStyles = t10;
  const originRef = useRef(null);
  let t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const node = originRef.current;
      if (!node) {
        return;
      }
      let offsetTop = 0;
      let offsetLeft = 0;
      const container = node.closest("dialog")?.firstElementChild;
      if (container) {
        const rec = container.getBoundingClientRect();
        offsetTop = rec.top;
        offsetLeft = rec.left;
      }
      const {
        top,
        left
      } = node.getBoundingClientRect();
      setOrigin({
        top: top - offsetTop,
        left: left - offsetLeft
      });
    };
    t12 = [];
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[22] !== duration || $[23] !== onCompleteRef) {
    t13 = () => {
      if (typeof onCompleteRef.current !== "function") {
        return;
      }
      const timeout = setTimeout(onCompleteRef.current, duration);
      return () => clearTimeout(timeout);
    };
    t14 = [duration, onCompleteRef];
    $[22] = duration;
    $[23] = onCompleteRef;
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  if ($[26] !== duration || $[27] !== force || $[28] !== keyframeStyles || $[29] !== origin || $[30] !== particleSize || $[31] !== particles || $[32] !== zIndex) {
    t15 = origin && createPortal(<div className={styles.screen} style={{
      ...rotationKeyframeStyles,
      ...keyframeStyles,
      ...(zIndex ? {
        zIndex
      } : null)
    }}><div style={{
        position: "absolute",
        top: origin.top,
        left: origin.left
      }}>{particles.map(particle_0 => <Particle key={particle_0.degree} particle={particle_0} duration={duration} force={force} particleSize={particleSize} />)}</div></div>, document.getElementById("bp-portal-target-inert") ?? document.body);
    $[26] = duration;
    $[27] = force;
    $[28] = keyframeStyles;
    $[29] = origin;
    $[30] = particleSize;
    $[31] = particles;
    $[32] = zIndex;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== props || $[35] !== t15) {
    t16 = <div ref={originRef} className={styles.container} {...props}>{t15}</div>;
    $[34] = props;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}
function _temp(particle) {
  return particle.degree;
}
export default Confetti;